import type { ReactNode } from "react";
import { ArrowDown, ArrowUp, Search, X } from "lucide-react";
import { cn, calcDelta } from "@/lib/utils";
import type { SortState } from "@/lib/wholesale-analytics-table";
import { DeltaBadge } from "@/components/ui/delta-badge";

/** Okvir sekcije analitike: naslov, kratko objašnjenje i akcija desno (sort, „prikaži sve"). */
export function Section({ title, hint, action, children, className }: {
  title: string; hint?: ReactNode; action?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section className={cn("ring-1 ring-foreground/10 min-w-0", className)}>
      <header className="px-3 py-2.5 border-b flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="min-w-0">
          <h3 className="text-xs font-medium">{title}</h3>
          {hint && <p className="text-[11px] text-muted-foreground mt-0.5 max-w-prose">{hint}</p>}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

export const TH = "py-2 px-2 font-medium";
export const THEAD_ROW = "border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground";

const INT = new Intl.NumberFormat("bs-BA", { maximumFractionDigits: 0 });
export const fmtInt = (n: number) => INT.format(n);
export const fmtShare = (n: number) => `${Math.round(n * 100)}%`;

/** Promjena u odnosu na prethodni period; bez prethodne vrijednosti piše „novo". */
export function Delta({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current > 0) return <span className="text-[10px] text-muted-foreground">novo</span>;
  return <DeltaBadge delta={calcDelta(current, previous)} />;
}

/** Segmentirani izbor (period, sort), isti izgled kao na maloprodajnoj analitici. */
export function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-0">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          onClick={() => onChange(o.value)}
          className={cn(
            "px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            value === o.value ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const HATCH = "repeating-linear-gradient(135deg, color-mix(in oklab, var(--foreground) 28%, transparent) 0 1px, transparent 1px 4px)";

/**
 * Sell-through kao traka zalihe: puni dio je ono što je VP prodala, šrafirani ostatak je
 * roba koja još leži na stanju. Bez vođene zalihe nema trake, samo crtica.
 */
export function SellThroughBar({ value, units, stock, className }: {
  value: number | null; units?: number; stock?: number | null; className?: string;
}) {
  if (value === null) {
    return <span className="text-muted-foreground" title="Zaliha se ne vodi u shopu">—</span>;
  }
  const pct = Math.round(value * 100);
  const title = units !== undefined && stock !== undefined && stock !== null
    ? `Prodano ${fmtInt(units)} kom, na stanju ${fmtInt(Math.max(0, stock))} kom`
    : undefined;
  return (
    <span className={cn("inline-flex items-center gap-2 justify-end", className)} title={title}>
      <span
        className="relative h-2 w-12 sm:w-20 overflow-hidden ring-1 ring-foreground/15"
        style={{ backgroundImage: HATCH }}
        role="img"
        aria-label={`Sell-through ${pct}%`}
      >
        <span className="absolute inset-y-0 left-0 bg-foreground" style={{ width: `${pct}%` }} />
      </span>
      <span className="tabular-nums w-9 text-right font-medium">{pct}%</span>
    </span>
  );
}

/** Traka udjela, skalirana prema najvećem udjelu u listi: prvi red je puna traka, pa se
 *  razlike vide i kad su svi udjeli mali. Broj pored nje je stvarni udio. */
export function ShareBar({ value, max }: { value: number; max: number }) {
  const w = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <span className="flex items-center gap-2">
      <span className="block h-1.5 flex-1 min-w-12 bg-foreground/[0.07]" aria-hidden>
        <span className="block h-full bg-foreground/45" style={{ width: `${w * 100}%` }} />
      </span>
      <span className="tabular-nums text-muted-foreground w-8 text-right">{fmtShare(value)}</span>
    </span>
  );
}

/** Zaglavlje kolone koje sortira tabelu: klik bira kolonu, ponovni klik okreće smjer.
 *  `className` nosi poravnanje i responsive skrivanje kao kod običnog `<th>`. */
export function SortTh<K extends string>({ k, sort, onSort, align = "right", className, children }: {
  k: K; sort: SortState<K>; onSort: (k: K) => void; align?: "left" | "right"; className?: string; children: ReactNode;
}) {
  const active = sort.key === k;
  const Arrow = sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      className={cn(TH, align === "right" ? "text-right" : "text-left", className)}
    >
      <button
        type="button"
        onClick={() => onSort(k)}
        className={cn(
          "inline-flex items-center gap-1 uppercase tracking-[0.08em] transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          align === "right" && "flex-row-reverse",
          active && "text-foreground",
        )}
      >
        {children}
        <Arrow className={cn("h-3 w-3 shrink-0", !active && "invisible")} aria-hidden />
      </button>
    </th>
  );
}

/** Polje za pretragu u zaglavlju sekcije, s dugmetom za brisanje upita. */
export function TableSearch({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder: string;
}) {
  return (
    <div className="relative w-full sm:w-56">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className="h-7 w-full border border-input bg-transparent pl-7 pr-7 text-xs outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Obriši pretragu"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

/** Dugme „Prikaži sve (n)" / „Prikaži manje" ispod skraćene liste. */
export function ShowAll({ open, total, onToggle }: { open: boolean; total: number; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="w-full border-t px-3 py-2 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
    >
      {open ? "Prikaži manje" : `Prikaži sve (${total})`}
    </button>
  );
}
